import 'components/ordering/quotation/components/overview/filter-slide-in.js'
import { html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, query, state } from 'lit/decorators.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { BREAKPOINTS, ConcreteBreakpoints } from '@cloudsuite/mosaic-shared/scripts/breakpoints.js'
import { overviewStyles } from 'components/ordering/quotation/components/quotation.style.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { QuotationStatus } from 'components/ordering/quotation/api/dtos/quotation-status.js'
import {
  deleteQuotationContext,
  listQuotationsContext,
  localeContext,
  quotationsDataContext
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { consume } from '@lit/context'
import { CSCQuotationOverviewFilterSlideIn } from 'components/ordering/quotation/components/overview/filter-slide-in.js'
import { redirectToPath } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'


initializeIconsByRole({
  'csc-quotation-overview.heading': { name: 'file-invoice-dollar', style: 'regular' },
  'csc-quotation-overview.filter': { name: 'filter', style: 'solid' },
  'csc-quotation-overview.action-view': { name: 'eye', style: 'solid' },
  'csc-quotation-overview.action-delete': { name: 'trash-alt', style: 'solid' },
})


type QuotationViewValue = {
  headerInternalAndExternalReference: {
    internalReference: string | null,
    externalReference: string | null,
    status: QuotationStatus
  },
  headerEmail: string,
  headerInternalReference: string | null,
  headerCreateDate: string,
  headerValidityDate: string | null,
  actions: any[]
}


const translations = {
  headerInternalAndExternalReference: () => t('header_internal_and_external_reference', {
    ns: 'csc-quotation-overview',
    default: 'Number/ERP Reference',
  }),
  headerEmail: () => t('header_email', {
    ns: 'csc-quotation-overview',
    default: 'User',
  }),
  headerInternalReference: () => t('header_internal_reference', {
    ns: 'csc-quotation-overview',
    default: 'Reference',
  }),
  headerCreateDate: () => t('header_create_date', {
    ns: 'csc-quotation-overview',
    default: 'Date',
  }),
  headerValidityDate: () => t('header_validity_date', {
    ns: 'csc-quotation-overview',
    default: 'Valid until',
  }),
  actionView: () => t('action_view', {
    ns: 'csc-quotation-overview',
    default: 'View quote',
  }),
  actionDelete: () => t('action_delete', {
    ns: 'csc-quotation-overview',
    default: 'Delete quote',
  }),
  noQuotationsFoundTitle: () => t('no_quotations_found_title', {
    ns: 'csc-quotation-overview',
    default: 'There are no quotations'
  }),
  noQuotationsFoundDescription: () => t('no_quotations_found_description', {
    ns: 'csc-quotation-overview',
    default: 'At the moment you don’t have any quotations to be shown.'
  }),
  noQuotationsFoundContactText: () => t('no_quotations_found_contact_text', {
    ns: 'csc-quotation-overview',
    default: 'Contact'
  }),
  quotationOverviewFilter: () => t('quotation_overview_filter', {
    ns: 'csc-quotation-overview',
    default: 'Filter quotations'
  }),
  quotationOverviewTitle: () => t('quotation_overview_title', {
    ns: 'csc-quotation-overview-title',
    default: 'Quotations',
  }),
} as const


@customElement('csc-quotation-overview')
export class CSCQuotationOverview extends LitElement {
  static override styles = [overviewStyles]

  @inject(BREAKPOINTS)
  private breakpoints!: ConcreteBreakpoints

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @state()
  private onSmallDevice: boolean = true

  @consume({ context: deleteQuotationContext })
  private deleteQuotation!: (quotationId: number) => Promise<void>

  @consume({ context: listQuotationsContext })
  private listQuotations!: () => Promise<void>

  @consume({ context: quotationsDataContext, subscribe: true })
  private quotationsData!: QuotationDTO[]

  @query('csc-quotation-overview-filter-slide-in')
  private filterSlideIn!: CSCQuotationOverviewFilterSlideIn | null | undefined

  @state()
  private firstFetchDone = false
  get noQuotationsFound() {
    return this.firstFetchDone && this.quotationsData.length === 0
  }

  get quotations(): QuotationViewValue[] {
    return this.quotationsData.map((quotation) => ({
      headerInternalAndExternalReference: {
        internalReference: quotation.internalReference,
        externalReference: quotation.externalReference,
        status: quotation.status
      },
      headerEmail: quotation.email,
      headerInternalReference: quotation.name,
      headerCreateDate: quotation.createDate,
      headerValidityDate: quotation.validityDate,
      actions: this.determineActions(quotation),
    }))
  }

  private get columns() {
    return [
      { name: translations.headerInternalAndExternalReference(), key: 'headerInternalAndExternalReference', width: 'auto', cellRenderer: this.referenceStatusRenderer },
      { name: translations.headerEmail(), key: 'headerEmail', width: 'auto' },
      { name: translations.headerInternalReference(), key: 'headerInternalReference', width: 'auto' },
      { name: translations.headerCreateDate(), key: 'headerCreateDate', width: 'auto', cellRenderer: this.dateRenderer },
      { name: translations.headerValidityDate(), key: 'headerValidityDate', width: 'auto', cellRenderer: this.dateRenderer },
      { name: '', key: 'actions', width: 'min-content', align: 'right', cellRenderer: this.actionsCellRenderer },
    ]
  }

  override async connectedCallback() {
    super.connectedCallback()

    createEffect(() => {
      this.onSmallDevice = this.breakpoints.small()
    })

    this.breakpoints.matchMedia(breakpoints.medium)

    await this.listQuotations()
    this.firstFetchDone = true
  }

  private referenceStatusRenderer = (item: {
    internalReference: string | null,
    externalReference: string | null,
    status: QuotationStatus
  }) => {
    const formattedReference = [item.internalReference, item.externalReference].filter(Boolean).join(' / ') || '-'

    return html`
      <cse-text>${formattedReference}</cse-text>
      <csc-quotation-status status="${item.status}"></csc-quotation-status>
    `
  }

  @consume({ context: localeContext })
  private locale!: Intl.LocalesArgument

  private dateRenderer = (date: string | null) => {
    if (!date || isNaN(new Date(date).getTime())) return nothing

    return new Intl.DateTimeFormat(this.locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date(date))
  }

  private actionOpen = async (item: QuotationDTO) => {
    await redirectToPath(this.windowLocation, `account/quotation/${item.id}/`)
  }

  private determineActions = (item: QuotationDTO) => {
    const actions: any[] = [
      {
        label: translations.actionView(),
        iconRole: 'csc-quotation-overview.action-view',
        action: () => this.actionOpen(item)
      }
    ]

    if (item.status === 'open' || item.status === 'draft') {
      actions.push({
        label: translations.actionDelete(),
        iconRole: 'csc-quotation-overview.action-delete',
        action: () => this.deleteQuotation(item.id)
      })
    }

    return actions
  }

  private actionsCellRenderer = (actions: any[]) => {
    if (actions.length === 0) return nothing
    if (!Array.isArray(actions)) return nothing

    return html`
      <cse-chip-group align="${this.onSmallDevice ? 'left' : 'right'}">
        ${actions.map(action => html`
          <cse-chip @click="${action.action}" title="${action.label}">
            <cse-icon icon-role="${action.iconRole}" slot="prefix"></cse-icon>
            ${this.onSmallDevice ? action.label : nothing}
          </cse-chip>
        `)}
      </cse-chip-group>
    `
  }

  private smallDeviceColumnRenderer = (item: any) => {
    const smallDeviceColumns = ['headerEmail', 'headerInternalReference', 'headerCreateDate', 'headerValidityDate']
    return html`
      <div class="columns">
        ${this.columns.map(column => {
          const key = column.key as keyof typeof translations
          const renderer = column.cellRenderer ? column.cellRenderer(item[key]) : item[key]
          
          return (smallDeviceColumns.includes(key)) ? html`
            <cse-text as="span" variant="caption-s">${column.name}</cse-text>
            <cse-text>
              ${renderer}
            </cse-text>
          ` : nothing
        })}
      </div>
    `
  }

  private renderSmallDeviceList() {
    return html`
      <cse-list>
        ${this.quotations.map(item => html`
          <cse-list-item>
            <cse-selectable>
              <div class="status">
                ${this.referenceStatusRenderer(item.headerInternalAndExternalReference)}
              </div>
              ${this.smallDeviceColumnRenderer(item)}
              ${this.actionsCellRenderer(item.actions)}
            </cse-selectable>
          </cse-list-item>
        `)}
      </cse-list>
    `
  }

  private renderNoQuotationsFound() {
    return html`
      <cse-alert variant="info">
        ${translations.noQuotationsFoundTitle()}
        <div slot="description">
          ${translations.noQuotationsFoundDescription()}
        </div>
      </cse-alert>
    `
  }

  private renderFilterChip() {
    return html`
      <cse-chip @click=${() => this.filterSlideIn?.open()}>
        <cse-icon icon-role="csc-quotation-overview.filter" slot="prefix"></cse-icon>
        ${translations.quotationOverviewFilter()}
      </cse-chip>
    `
  }

  private renderTopChips = (): TemplateResult | typeof nothing => {
    if (this.noQuotationsFound || this.onSmallDevice) return nothing
    return this.renderFilterChip()
  }

  private renderMobileActionBar(): TemplateResult | typeof nothing {
    if (this.noQuotationsFound || !this.onSmallDevice) return nothing
    return html`
      <div class="mobile-action-bar">
        ${this.renderFilterChip()}
      </div>
    `
  }

  private renderQuotationTable = (): TemplateResult => {
    if (this.noQuotationsFound) return this.renderNoQuotationsFound()
    if (this.onSmallDevice) return this.renderSmallDeviceList()

    return html`
      <csc-table .columns="${this.columns}" .data="${this.quotations}" variant="secondary"></csc-table>
    `
  }

  override render() {
    return html`
      <csc-account-header heading-icon-role="csc-quotation-overview.heading" hide-divider>
        <span slot="heading">
          ${translations.quotationOverviewTitle()}
        </span>

        <span slot="top-chips">
          ${this.renderTopChips()}
        </span>
      </csc-account-header>

      ${this.renderMobileActionBar()}

      ${!this.noQuotationsFound ? html`<csc-quotation-overview-filter-slide-in></csc-quotation-overview-filter-slide-in>` : nothing}

      ${this.renderQuotationTable()}
    `
  }
}
